import { useEffect, useRef, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client.js';
import { queryKeys } from '../lib/queryKeys';

function safeParse (raw) {
  try { return JSON.parse(raw); } catch { return null; }
}

/**
 * SSE-based TanStack Query cache invalidation.
 *
 * Connects to /api/realtime, listens for lead, message, activity, notification and inbox events,
 * and invalidates or updates the query cache accordingly. Lead, inbox and monitoring
 * invalidations are batched: multiple SSE events within a 2s window produce one refetch
 * per query key, preventing table flicker.
 */
export function useRealtimeInvalidation (token, callbacks = {}) {
  const qc = useQueryClient();
  const reconnectAttempt = useRef(0);
  const sourceRef = useRef(null);
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  const pendingKeys = useRef(new Set());
  const flushTimer = useRef(null);
  const DEBOUNCE_MS = 2000;

  const scheduleInvalidation = useCallback((...keys) => {
    for (const k of keys) pendingKeys.current.add(JSON.stringify(k));
    if (flushTimer.current) return;
    flushTimer.current = setTimeout(() => {
      const snapshot = [...pendingKeys.current];
      pendingKeys.current.clear();
      flushTimer.current = null;
      for (const raw of snapshot) {
        qc.invalidateQueries({ queryKey: JSON.parse(raw) });
      }
    }, DEBOUNCE_MS);
  }, [qc]);

  const [reconnectTick, setReconnectTick] = useState(0);

  const attachListeners = useCallback((source) => {
    source.addEventListener('lead:created', (event) => {
      const payload = safeParse(event.data);
      if (!payload?.lead) return;
      scheduleInvalidation(
        ['leads', 'column'],
        ['leads', 'bloggers'],
        queryKeys.leads.boardStats()
      );
    });

    source.addEventListener('lead:updated', (event) => {
      const payload = safeParse(event.data);
      if (!payload?.lead) return;
      const lead = payload.lead;
      qc.setQueryData(queryKeys.leads.detail(lead.id), (old) =>
        old ? { ...old, ...lead } : lead
      );
      scheduleInvalidation(
        ['leads', 'column'],
        ['leads', 'bloggers'],
        queryKeys.leads.boardStats()
      );
    });

    source.addEventListener('lead:deleted', (event) => {
      const payload = safeParse(event.data);
      const leadId = payload?.leadId;
      if (!leadId) return;
      qc.removeQueries({ queryKey: queryKeys.leads.detail(leadId) });
      scheduleInvalidation(
        ['leads', 'column'],
        ['leads', 'bloggers'],
        queryKeys.leads.boardStats()
      );
      callbacksRef.current.onLeadDeleted?.(leadId);
    });

    source.addEventListener('message:created', (event) => {
      const payload = safeParse(event.data);
      if (!payload) return;
      const lead = payload.lead;
      const message = payload.message;
      const targetLeadId = message?.leadId || lead?.id;

      if (lead) {
        qc.setQueryData(queryKeys.leads.detail(lead.id), (old) =>
          old ? { ...old, ...lead } : lead
        );
        scheduleInvalidation(
          ['leads', 'column'],
          ['leads', 'bloggers']
        );
      }
      if (targetLeadId) {
        if (message) {
          qc.setQueryData(queryKeys.messages.byLead(targetLeadId), (old) => {
            const current = Array.isArray(old) ? old : [];
            const hasSameId = message.id
              ? current.some(item => item?.id === message.id)
              : false;
            if (hasSameId) return current;

            const next = [...current, message];
            next.sort((a, b) => {
              const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
              const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
              return aTime - bTime;
            });
            return next;
          });
        }
        qc.invalidateQueries({ queryKey: queryKeys.messages.byLead(targetLeadId) });
        callbacksRef.current.onTimerExpire?.(targetLeadId);
      }
      scheduleInvalidation(queryKeys.leads.unreadStatus());
    });

    source.addEventListener('lead:activity', (event) => {
      const payload = safeParse(event.data);
      if (!payload?.activity) return;
      const leadId = payload.leadId || payload.activity?.leadId;
      if (leadId) {
        callbacksRef.current.onActivity?.({ leadId, activity: payload.activity });
      }
    });

    source.addEventListener('notification:created', (event) => {
      const payload = safeParse(event.data);
      if (!payload?.notification) return;

      qc.invalidateQueries({ queryKey: queryKeys.notifications.all });

      if (payload.notification.channel === 'monitoring') {
        const notifLeadId = payload.notification.leadId;
        if (notifLeadId) {
          scheduleInvalidation(['monitoring', notifLeadId], ['monitoringList']);
        } else {
          scheduleInvalidation(['monitoring'], ['monitoringList']);
        }
      }

      if (document.hidden && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        const n = payload.notification;
        try {
          const browserNotif = new Notification(n.title || 'Новое сообщение', {
            body: n.body || '',
            tag: `crm-notif-${n.id}`,
            icon: '/favicon.ico',
          });
          browserNotif.onclick = () => {
            window.focus();
            browserNotif.close();
          };
        } catch (_err) {
          // Notification API may fail in some contexts
        }
      }
    });

    source.addEventListener('notification:archived', (event) => {
      const payload = safeParse(event.data);
      if (!payload?.id) return;
      qc.invalidateQueries({ queryKey: queryKeys.notifications.all });
    });

    source.addEventListener('notification:read', (event) => {
      const payload = safeParse(event.data);
      if (!payload?.id) return;
      qc.invalidateQueries({ queryKey: queryKeys.notifications.all });
    });

    source.addEventListener('notification:unread', (event) => {
      const payload = safeParse(event.data);
      if (!payload?.id) return;
      qc.invalidateQueries({ queryKey: queryKeys.notifications.all });
    });

    source.addEventListener('inbox:row_updated', (event) => {
      const payload = safeParse(event.data);
      if (!payload?.leadId) return;
      scheduleInvalidation(queryKeys.inbox.all);
    });
  }, [qc, scheduleInvalidation]);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    let reconnectTimer = null;
    const abortController = new AbortController();

    const scheduleReconnect = () => {
      const delay = Math.min(3000 * (reconnectAttempt.current + 1), 30000);
      reconnectAttempt.current += 1;
      reconnectTimer = setTimeout(() => {
        if (!cancelled) setReconnectTick(t => t + 1);
      }, delay);
    };

    const open = async () => {
      let ticket;
      try {
        // Body must be `{}`, not `null`: express's strict JSON body parser rejects a literal `null`.
        const res = await apiClient.post('/realtime/ticket', {}, { signal: abortController.signal });
        ticket = res?.data?.ticket;
      } catch (err) {
        if (cancelled || abortController.signal.aborted) return;
        if (err?.status === 401 || err?.originalError?.response?.status === 401) return;
        scheduleReconnect();
        return;
      }

      if (cancelled || abortController.signal.aborted || !ticket) return;

      const source = new EventSource(`/api/realtime?ticket=${encodeURIComponent(ticket)}`);
      sourceRef.current = source;

      attachListeners(source);

      source.onopen = () => {
        reconnectAttempt.current = 0;
      };

      source.onerror = () => {
        source.close();
        if (sourceRef.current === source) sourceRef.current = null;
        if (cancelled) return;
        scheduleReconnect();
      };
    };

    open();

    return () => {
      cancelled = true;
      abortController.abort();
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (flushTimer.current) {
        clearTimeout(flushTimer.current);
        flushTimer.current = null;
      }
      sourceRef.current?.close?.();
      sourceRef.current = null;
    };
  }, [token, reconnectTick, attachListeners]);
}
